import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Integration, StandardPipelineInfo } from '../../../shared/interfaces/integration';
import { Source, SourceType } from '../../../shared/interfaces/source';
import { Destination, DestinationType } from '../../../shared/interfaces/destination';
import { useQuery } from '../../../request/hooks/useQuery';
import { PipelineAPI } from '../../api/PipelineAPI';

interface CreatePipelineContextState {
  pipeline: Integration | null;
  source: Source | null;
  sourceType: SourceType | null;
  destination: Destination | null;
  destinationType: DestinationType | null;
  standardPipelineInfo: StandardPipelineInfo | undefined;
  isUpgradingPipeline: boolean;
}

interface CreatePipelineContextActions {
  updateState: (state: Partial<CreatePipelineContextState>) => void;
  updatePipeline: (pipeline: Integration) => void;
  updateSource: (source: Source) => void;
  updateSourceType: (sourceType: SourceType) => void;
  updateDestination: (destination: Destination) => void;
  updateDestinationType: (destinationType: DestinationType) => void;
}

export type CreatePipelineContextReturnType = CreatePipelineContextState &
  CreatePipelineContextActions;

const initialState: CreatePipelineContextState = {
  pipeline: null,
  source: null,
  destination: null,
  sourceType: null,
  destinationType: null,
  standardPipelineInfo: undefined,
  isUpgradingPipeline: false
};

export const CreatePipelineContext = createContext<CreatePipelineContextReturnType>({
  ...initialState,
  updateState: () => {},
  updatePipeline: () => {},
  updateSource: () => {},
  updateSourceType: () => {},
  updateDestination: () => {},
  updateDestinationType: () => {}
});

export function CreatePipelineContextProvider({ children }: PropsWithChildren) {
  const [searchParams] = useSearchParams();
  const standardPipelineSeqId = searchParams.get('seq_id');
  const isUpgradingPipeline = searchParams.get('upgrading_pipeline') === 'true';
  const [state, setState] = useState({ ...initialState, isUpgradingPipeline });

  const { fetch: fetchUpgradingPipelineInfo } = useQuery({
    dataSource$: () =>
      PipelineAPI.getV1Pipeline(Number(standardPipelineSeqId)).then(res =>
        setState(prevState => ({ ...prevState, standardPipelineInfo: res.data }))
      )
  });

  useEffect(() => {
    if (isUpgradingPipeline && standardPipelineSeqId) {
      fetchUpgradingPipelineInfo();
    }
  }, [isUpgradingPipeline, standardPipelineSeqId]);

  const updateState = useCallback((_state: Partial<CreatePipelineContextState>) => {
    setState(prevState => ({ ...prevState, ..._state }));
  }, []);

  const updatePipeline = useCallback((pipeline: Integration) => {
    setState(prevState => ({ ...prevState, pipeline }));
  }, []);

  const updateSource = useCallback((source: Source) => {
    setState(prevState => ({ ...prevState, source }));
  }, []);

  const updateSourceType = useCallback((sourceType: SourceType) => {
    setState(prevState => ({ ...prevState, sourceType }));
  }, []);

  const updateDestination = useCallback((destination: Destination) => {
    setState(prevState => ({ ...prevState, destination }));
  }, []);

  const updateDestinationType = useCallback((destinationType: DestinationType) => {
    setState(prevState => ({ ...prevState, destinationType }));
  }, []);

  const providerValues: CreatePipelineContextState & CreatePipelineContextActions = useMemo(() => {
    return {
      ...state,
      updateState,
      updatePipeline,
      updateSource,
      updateSourceType,
      updateDestination,
      updateDestinationType
    };
  }, [
    state,
    updateState,
    updatePipeline,
    updateSource,
    updateSourceType,
    updateDestination,
    updateDestinationType
  ]);

  return (
    <CreatePipelineContext.Provider value={providerValues}>
      {children}
    </CreatePipelineContext.Provider>
  );
}
